import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { isAxiosError } from 'axios';
import { Plus } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { Modal } from '@/components/Modal/Modal';
import { Table } from '@/components/Table/Table';
import {
  createFamilia,
  createGrupo,
  createTipoProduto,
  createUnidadeMedida,
  deleteFamilia,
  deleteGrupo,
  deleteTipoProduto,
  deleteUnidadeMedida,
  fetchFamilias,
  fetchGrupos,
  fetchTiposProduto,
  fetchUnidadesMedida,
  produtoKeys,
  updateFamilia,
  updateGrupo,
  updateTipoProduto,
  updateUnidadeMedida,
} from '@/services/produtoService';
import type { ICatalogoItem, ICatalogoItemCreate } from '@/types/produto';
import { readStoredUser } from '@/utils/tenantScope';
import './catalogo-produto.css';

export const Route = createFileRoute('/_authenticated/configuracao/catalogo-produto/')({
  component: CatalogoProdutoPage,
});

type TCatalogTab = 'familia' | 'grupo' | 'tipo' | 'um';

const TABS: { id: TCatalogTab; label: string; codMaxLength?: number }[] = [
  { id: 'familia', label: 'Famílias', codMaxLength: 2 },
  { id: 'grupo', label: 'Grupos' },
  { id: 'tipo', label: 'Tipos de produto' },
  { id: 'um', label: 'Unidades de medida' },
];

function extractApiError(error: unknown, fallback: string): string {
  if (isAxiosError(error)) {
    const data = error.response?.data as { error?: string | { message?: string } } | undefined;
    if (typeof data?.error === 'string') return data.error;
    if (data?.error && typeof data.error === 'object' && data.error.message) {
      return data.error.message;
    }
    return error.message;
  }
  if (error instanceof Error) return error.message;
  return fallback;
}

function canWrite(): boolean {
  const role = readStoredUser()?.role;
  return role === 'tenant_admin' || role === 'super_admin';
}

function tabQueryKey(tab: TCatalogTab) {
  if (tab === 'familia') return produtoKeys.familias();
  if (tab === 'grupo') return produtoKeys.grupos();
  if (tab === 'tipo') return produtoKeys.tipos();
  return produtoKeys.unidadesMedida();
}

function fetchTab(tab: TCatalogTab): Promise<ICatalogoItem[]> {
  if (tab === 'familia') return fetchFamilias();
  if (tab === 'grupo') return fetchGrupos();
  if (tab === 'tipo') return fetchTiposProduto();
  return fetchUnidadesMedida();
}

function createTab(tab: TCatalogTab, payload: ICatalogoItemCreate): Promise<ICatalogoItem> {
  if (tab === 'familia') return createFamilia(payload);
  if (tab === 'grupo') return createGrupo(payload);
  if (tab === 'tipo') return createTipoProduto(payload);
  return createUnidadeMedida(payload);
}

function updateTab(tab: TCatalogTab, id: string, nome: string): Promise<ICatalogoItem> {
  const payload = { nome };
  if (tab === 'familia') return updateFamilia(id, payload);
  if (tab === 'grupo') return updateGrupo(id, payload);
  if (tab === 'tipo') return updateTipoProduto(id, payload);
  return updateUnidadeMedida(id, payload);
}

function deleteTab(tab: TCatalogTab, id: string): Promise<void> {
  if (tab === 'familia') return deleteFamilia(id);
  if (tab === 'grupo') return deleteGrupo(id);
  if (tab === 'tipo') return deleteTipoProduto(id);
  return deleteUnidadeMedida(id);
}

function tabTitle(tab: TCatalogTab, editing: boolean): string {
  const names: Record<TCatalogTab, [string, string]> = {
    familia: ['Nova família', 'Editar família'],
    grupo: ['Novo grupo', 'Editar grupo'],
    tipo: ['Novo tipo de produto', 'Editar tipo de produto'],
    um: ['Nova unidade de medida', 'Editar unidade de medida'],
  };
  return names[tab][editing ? 1 : 0];
}

function CatalogoProdutoPage() {
  const queryClient = useQueryClient();
  const writeOk = canWrite();

  const [activeTab, setActiveTab] = useState<TCatalogTab>('familia');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<ICatalogoItem | null>(null);
  const [cod, setCod] = useState('');
  const [nome, setNome] = useState('');
  const [formError, setFormError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const activeTabMeta = TABS.find((t) => t.id === activeTab)!;

  const { data = [], isLoading, isError, error } = useQuery({
    queryKey: tabQueryKey(activeTab),
    queryFn: () => fetchTab(activeTab),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: produtoKeys.all });
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (editing) {
        return updateTab(activeTab, editing.id, nome.trim());
      }
      return createTab(activeTab, { cod: cod.trim(), nome: nome.trim() });
    },
    onSuccess: () => {
      invalidate();
      closeModal();
      setSuccessMessage(editing ? 'Registro atualizado.' : 'Registro cadastrado.');
    },
    onError: (err) => setFormError(extractApiError(err, 'Falha ao salvar.')),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteTab(activeTab, id),
    onSuccess: () => {
      invalidate();
      setSuccessMessage('Registro excluído.');
      setFormError('');
    },
    onError: (err) => {
      setSuccessMessage('');
      setFormError(extractApiError(err, 'Falha ao excluir.'));
    },
  });

  const openCreate = () => {
    setEditing(null);
    setCod('');
    setNome('');
    setFormError('');
    setModalOpen(true);
  };

  const openEdit = (row: ICatalogoItem) => {
    setEditing(row);
    setCod(row.cod);
    setNome(row.nome);
    setFormError('');
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
    setFormError('');
    setCod('');
    setNome('');
  };

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    setFormError('');
    const nomeTrim = nome.trim();
    if (!nomeTrim) {
      setFormError('Informe o nome.');
      return;
    }
    if (!editing) {
      const codTrim = cod.trim();
      if (!codTrim) {
        setFormError('Informe o código.');
        return;
      }
      if (activeTab === 'familia' && codTrim.length !== 2) {
        setFormError('Código da família deve ter 2 caracteres.');
        return;
      }
    }
    saveMutation.mutate();
  };

  const onDelete = (row: ICatalogoItem) => {
    if (!window.confirm(`Excluir "${row.nome}" (${row.cod})?`)) return;
    deleteMutation.mutate(row.id);
  };

  const colSpan = writeOk ? 3 : 2;
  const saving = saveMutation.isPending || deleteMutation.isPending;

  return (
    <div className="catalogo-produto-page">
      <div className="catalogo-produto-page__header">
        <div>
          <h1>Parâmetros do catálogo</h1>
          <p className="catalogo-produto-page__subtitle">
            Famílias, grupos, tipos e unidades de medida usados no cadastro de produtos WMS.
          </p>
        </div>
      </div>

      <p className="catalogo-produto-page__hint">
        Categorias/agrupadores excluídos da integração ERP ficam em{' '}
        <Link to="/configuracao/integracoes">Integrações</Link> (por integração).
      </p>

      <div className="catalogo-produto-page__tabs" role="tablist" aria-label="Tipo de parâmetro">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.id}
            className={[
              'catalogo-produto-page__tab',
              activeTab === tab.id ? 'catalogo-produto-page__tab--active' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            onClick={() => {
              setActiveTab(tab.id);
              setSuccessMessage('');
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {successMessage && (
        <p className="catalogo-produto-page__success" role="status">
          {successMessage}
        </p>
      )}
      {formError && !modalOpen && (
        <p className="catalogo-produto-page__error" role="alert">
          {formError}
        </p>
      )}
      {isError && (
        <p className="catalogo-produto-page__error" role="alert">
          {extractApiError(error, 'Falha ao carregar registros.')}
        </p>
      )}

      {writeOk && (
        <div className="catalogo-produto-page__toolbar">
          <button
            type="button"
            className="catalogo-produto-page__btn-primary"
            disabled={saving}
            onClick={openCreate}
          >
            <Plus size={16} /> Novo
          </button>
        </div>
      )}

      <div className="catalogo-produto-page__table-container">
        <Table>
          <Table.Header>
            <Table.Row>
              <Table.Head scope="col">Código</Table.Head>
              <Table.Head scope="col">Nome</Table.Head>
              {writeOk && <Table.Head scope="col">Ações</Table.Head>}
            </Table.Row>
          </Table.Header>
          <Table.Content>
            {isLoading && (
              <Table.Row>
                <Table.Cell colSpan={colSpan}>Carregando…</Table.Cell>
              </Table.Row>
            )}
            {!isLoading && data.length === 0 && (
              <Table.Row>
                <Table.Cell colSpan={colSpan}>Nenhum registro cadastrado.</Table.Cell>
              </Table.Row>
            )}
            {data.map((row) => (
              <Table.Row key={row.id}>
                <Table.Cell>{row.cod}</Table.Cell>
                <Table.Cell>{row.nome}</Table.Cell>
                {writeOk && (
                  <Table.Cell className="catalogo-produto-page__actions">
                    <button type="button" onClick={() => openEdit(row)}>
                      Editar
                    </button>
                    <button type="button" data-variant="danger" onClick={() => onDelete(row)}>
                      Excluir
                    </button>
                  </Table.Cell>
                )}
              </Table.Row>
            ))}
          </Table.Content>
        </Table>
      </div>

      {modalOpen && (
        <Modal onClose={closeModal} className="catalogo-produto-page__modal">
          <Modal.Header title={tabTitle(activeTab, Boolean(editing))} />
          <Modal.Content>
            <form id="catalogo-param-form" className="catalogo-produto-page__form" onSubmit={onSubmit}>
              {formError && (
                <p className="catalogo-produto-page__error" role="alert">
                  {formError}
                </p>
              )}
              <label htmlFor="catalogo-cod">
                Código
                <input
                  id="catalogo-cod"
                  value={cod}
                  onChange={(e) => setCod(e.target.value.toUpperCase())}
                  maxLength={activeTabMeta.codMaxLength ?? 10}
                  required={!editing}
                  disabled={Boolean(editing)}
                />
              </label>
              <label htmlFor="catalogo-nome">
                Nome
                <input
                  id="catalogo-nome"
                  value={nome}
                  onChange={(e) => setNome(e.target.value)}
                  required
                  autoFocus
                />
              </label>
            </form>
          </Modal.Content>
          <Modal.Footer>
            <button
              type="button"
              className="catalogo-produto-page__btn-secondary"
              onClick={closeModal}
              disabled={saving}
            >
              Cancelar
            </button>
            <button
              type="submit"
              form="catalogo-param-form"
              className="catalogo-produto-page__btn-primary"
              disabled={saving}
            >
              {saving ? 'Salvando…' : editing ? 'Salvar' : 'Cadastrar'}
            </button>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
}
